/* eslint-disable react/no-unescaped-entities, react/jsx-key */
import { Check, ArrowUpRight, Banknote, ShieldCheck } from "lucide-react";

const features = [
  "Site complet sur-mesure",
  "Design responsive mobile parfait",
  "SEO local de base intégré",
  "Nom de domaine inclus",
  "Mise en ligne & hébergement",
  "Formulaire de contact qualifié",
  "Calendrier de prise de rendez-vous",
  "Optimisation Core Web Vitals",
];

const maintenanceExamples = [
  "Changement de numéro de téléphone",
  "Modification des horaires",
  "Ajout de nouvelles photos",
  "Modification d'un texte",
  "Ajout d'un nouveau service",
  "Mise à jour des coordonnées",
  "Correction d'erreurs",
  "Ajout d'une actualité",
];

export const Pricing = () => (
  <section id="tarifs" data-testid="pricing-section" className="py-24 md:py-32 bg-paper-soft border-y border-ink/5">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="mb-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7">
          <p className="font-mono-label text-ink/60"><span className="rule" />Tarifs transparents</p>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl text-ink tracking-tight leading-[1.05]">
            Un tarif clair, <em className="italic font-light">pensé pour les entreprises locales</em>.
          </h2>
        </div>
        <p className="lg:col-span-4 text-ink/65 leading-relaxed">
          Pas de surprise, pas de frais cachés. Vous savez exactement ce que
          vous payez et ce que vous recevez.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <article
          data-testid="pricing-main"
          className="lg:col-span-7 bg-white border border-ink/10 p-10 md:p-14 relative lift"
        >
          {/* Cyan corner accent */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-cyan/20 to-transparent pointer-events-none" aria-hidden />

          <div className="flex items-start justify-between flex-wrap gap-4 relative">
            <div>
              <p className="font-mono-label text-ink/55">Offre principale</p>
              <h3 className="mt-3 font-display text-3xl md:text-4xl text-ink">Pack Site Web Professionnel</h3>
            </div>
            <span className="text-xs px-3 py-1 btn-premium">Le plus demandé</span>
          </div>

          <div className="mt-8 flex items-baseline gap-3">
            <span className="font-mono-label text-ink/55">À partir de</span>
            <span className="font-display text-6xl md:text-7xl tracking-tight bg-gradient-to-br from-ink to-cyan-600 bg-clip-text text-transparent">1000 €*</span>
          </div>

          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            {features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-ink/85">
                <span className="mt-1 h-4 w-4 rounded-full bg-cyan/15 text-cyan-600 inline-flex items-center justify-center shrink-0">
                  <Check size={11} strokeWidth={3} />
                </span>
                <span className="text-sm">{f}</span>
              </li>
            ))}
          </ul>

          {/* Payment terms */}
          <div className="mt-10 bg-paper-soft border-l-2 border-cyan p-5" data-testid="pricing-payment-terms">
            <div className="flex items-center gap-2 font-mono-label text-ink/60 mb-3">
              <Banknote size={14} className="text-cyan-600" />
              Modalités de paiement
            </div>
            <ul className="space-y-2 text-sm text-ink/80">
              <li className="flex items-center justify-between">
                <span>Acompte au démarrage</span>
                <span className="font-display text-xl text-ink">30 %</span>
              </li>
              <li className="flex items-center justify-between">
                <span>Solde à la livraison du projet</span>
                <span className="font-display text-xl text-ink">70 %</span>
              </li>
              <li className="pt-3 mt-3 border-t border-ink/10 text-ink/65 text-xs flex items-center gap-2">
                <ShieldCheck size={14} className="text-cyan-600" />
                Paiement par virement bancaire — devis ferme avant signature.
              </li>
            </ul>
          </div>

          <a
            href="#contact"
            data-testid="pricing-cta-main"
            className="mt-10 inline-flex items-center gap-3 h-14 px-7 btn-premium text-sm font-medium group"
          >
            Prendre un rendez-vous
            <ArrowUpRight size={18} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
          </a>
        </article>

        <div className="lg:col-span-5 grid grid-cols-1 gap-6">
          <div className="bg-white border border-ink/10 p-8 md:p-10 lift" data-testid="pricing-maintenance">
            <p className="font-mono-label text-ink/55">Maintenance mensuelle</p>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="font-display text-5xl bg-gradient-to-br from-ink to-cyan-600 bg-clip-text text-transparent">30 €*</span>
              <span className="text-sm text-ink/55">/ mois</span>
            </div>
            <p className="mt-3 text-sm text-ink/75 leading-relaxed">
              Vous gardez un site à jour <strong className="text-ink">sans avoir à vous en occuper</strong>.
            </p>
            <p className="mt-6 font-mono-label text-ink/55">Modifications incluses</p>
            <ul className="mt-3 grid grid-cols-1 gap-1.5 text-sm text-ink/75">
              {maintenanceExamples.map((m) => (
                <li key={m} className="flex items-start gap-2">
                  <span className="text-cyan-600 mt-0.5">✓</span>
                  <span>{m}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-gradient-to-br from-ink to-ink-600 text-white p-8 md:p-10 relative overflow-hidden lift">
            <div className="absolute -top-12 -right-12 w-44 h-44 bg-cyan rounded-full opacity-25 blur-3xl" aria-hidden />
            <p className="font-mono-label text-white/65 relative">Automatisations & IA</p>
            <p className="mt-4 font-display text-3xl relative">Sur devis</p>
            <p className="mt-3 text-sm text-white/75 leading-relaxed relative">
              Chatbots IA, agents vocaux, intégrations CRM, workflows
              n8n/Zapier — chiffrés en fonction de la complexité du flux et
              des outils connectés.
            </p>
            <a href="#contact" data-testid="pricing-cta-ai" className="mt-6 inline-flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300 link-under relative">
              Discuter d'un projet IA →
            </a>
          </div>
        </div>
      </div>

      <p className="mt-8 text-xs text-ink/55" data-testid="pricing-tva-note">
        <span aria-hidden="true">*</span> TVA non applicable — vous ne payez que le prix affiché.
      </p>
    </div>
  </section>
);
